import React, { useMemo } from "react";
import CountUp from "react-countup";
import { useInView } from "react-intersection-observer";
import Tilt from "react-parallax-tilt";
import { motion } from "framer-motion";

// Icons
import { HiOutlineTrophy } from "react-icons/hi2";
import { TbLayoutDashboard } from "react-icons/tb";
import { MdDesignServices, MdHandshake, MdRocketLaunch } from "react-icons/md";
import { FaUserFriends } from "react-icons/fa";

// Single Stat Card Component
const StatCard = ({ icon: Icon, value, label, direction = "left" }) => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.3 });

  const [numericValue, suffix] = useMemo(() => {
    const num = parseInt(value.replace(/\D/g, ""));
    const suf = value.replace(/[0-9,]/g, "");
    return [num, suf];
  }, [value]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: direction === "left" ? -80 : 80 }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.8, ease: "easeOut" }}
    >
      <Tilt
        tiltMaxAngleX={5}
        tiltMaxAngleY={5}
        glareEnable={false}
        className="w-full"
      >
        <div className="relative group rounded-lg bg-white bg-opacity-20 backdrop-blur-md border border-orange-800 transition-all hover:shadow-orange-300 overflow-hidden px-4 py-6 flex flex-col items-center justify-center text-center min-h-[160px]">
          {/* Glow effect */}
          <div className="absolute inset-0 z-0 rounded-lg bg-gradient-to-br from-orange-700 via-transparent to-orange-900 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

          {/* Icon */}
          <div className="relative z-10 text-center text-orange-800 text-4xl mb-2">
            <Icon />
          </div>

          {/* Number */}
          <h2 className="relative z-10 text-4xl text-center font-bold text-orange-800 drop-shadow-sm">
            {inView ? <CountUp separator="" end={numericValue} duration={2} delay={0.2} /> : 0}
            {suffix}
          </h2>

          {/* Divider */}
          <div className="relative z-10 w-8 h-[2px] bg-orange-800 my-2" />

          {/* Label */}
          <p className="relative z-10 font-semibold text-center text-gray-700 text-sm tracking-wide">
            {label}
          </p>
        </div>
      </Tilt>
    </motion.div>
  );
};

// Stats Section Component
const StatsSection = () => {
  const stats = [
    { icon: HiOutlineTrophy, value: "10+", label: "Years of Expertise", direction: "left" },
    { icon: TbLayoutDashboard, value: "150+", label: "Projects", direction: "right" },
    { icon: MdDesignServices, value: "2000+", label: "Designs", direction: "left" },
    { icon: MdHandshake, value: "500+", label: "Dealers", direction: "right" },
    { icon: FaUserFriends, value: "100+", label: "Happy Customers", direction: "left" },
    { icon: MdRocketLaunch, value: "2005", label: "Founded", direction: "right" },
  ];

  return (
    <section className="relative py-20 max-w-[1200px] mx-auto overflow-hidden">
      <div className="relative z-10 text-center mb-5">
        <h2 className="text-2xl md:text-4xl font-bold text-gray-800 tracking-tight">
          Navkar Laminates in Numbers
        </h2>
      </div>

      <p className="text-gray-600 max-w-2xl text-center mb-10 mx-auto text-sm md:text-base">
        From industry presence to satisfied clients, these numbers reflect our commitment to quality, innovation, and excellence in the laminates industry.
      </p>

      <div className="relative z-10 max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 px-4">
        {stats.map((stat, index) => (
          <StatCard key={index} {...stat} />
        ))}
      </div>
    </section>
  );
};

export default StatsSection;
